import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    linkedSignal,
    Signal,
    signal,
    viewChild,
    viewChildren,
} from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';

import { ImageDataDb } from '../_shared/data/image-data-db.data';
import {
    IDynamicElement,
    ImageData,
    IProductFeatureExplorer,
    IProductFeatureExplorerHotspot,
    IProductFeatureExplorerItem,
    MediaImage,
} from '../_shared/models';
import { getRovingIndex } from '../_shared/utils/roving-index';
import { withConfigFallback } from '../_shared/utils/with-config-fallback';
import { DISCLOSURE_TRIGGER_SELECTOR, DisclosureComponent } from '../disclosure/disclosure.component';
import { HeadingComponent } from '../heading/heading.component';
import { ImageItemComponent } from '../media/media-item/image-item/image-item.component';
import { TextHtmlComponent } from '../text-html/text-html.component';

let nextExplorerId = 0;

// The automatic pulse tour must finish within WCAG 2.2.2's 5-second budget for
// auto-started motion; 4.75s leaves margin. Total = (N-1)·step + duration, and
// with step = min(cap, budget / (N + ratio - 1)) that stays inside the budget
// for every marker count while consecutive rings still overlap.
const AUTO_PULSE_BUDGET_SECONDS = 4.75;
const MAX_PULSE_STEP_SECONDS = 0.8;
const MAX_PULSE_SECONDS = 2;
const PULSE_DURATION_RATIO = 2.5;

/** A feature as the template consumes it: one panel, optionally marked on the image. */
interface FeaturePanel {
    index: number;
    number: number;
    title: string;
    teaser?: string;
    detailsHtml?: string;
    hotspot?: IProductFeatureExplorerHotspot | null;
    regionId: string;
    markerLabel: string;
}

type HotspotFeaturePanel = FeaturePanel & { hotspot: IProductFeatureExplorerHotspot };

function hasHotspot(panel: FeaturePanel): panel is HotspotFeaturePanel {
    return !!panel.hotspot;
}

/**
 * Product Feature Explorer: always-visible feature panels either side of a
 * product render, numbered markers on the image, one panel open at a time. The
 * markers are positioned with pure CSS percentages inside a stage whose
 * `aspect-ratio` comes from the config — no DOM measurement, so the SSR HTML is
 * already correct.
 */
@Component({
    selector: 'sirius-product-feature-explorer',
    imports: [DisclosureComponent, HeadingComponent, ImageItemComponent, TextHtmlComponent, TranslateModule],
    templateUrl: './product-feature-explorer.component.html',
    styleUrl: './product-feature-explorer.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        'class': 'sir-product-feature-explorer',
        '[class.is-tour-running]': 'tourRunning()',
        '[style.--feature-explorer-pulse-step]': 'pulseTiming().step',
        '[style.--feature-explorer-pulse-duration]': 'pulseTiming().duration',
        '(keydown)': 'onKeydown($event)',
    },
})
export class ProductFeatureExplorerComponent implements IDynamicElement<IProductFeatureExplorer> {
    readonly config = input<IProductFeatureExplorer | undefined>(undefined);
    readonly title = input<string | undefined>(undefined);
    readonly image = input<MediaImage | undefined>(undefined);
    readonly imageAspectRatio = input<number | undefined>(undefined);
    readonly items = input<IProductFeatureExplorerItem[] | undefined>(undefined);

    protected readonly resolvedTitle = withConfigFallback(this.config, 'title', this.title);
    protected readonly resolvedImage = withConfigFallback(this.config, 'image', this.image);

    /** Responsive fit-in bounding boxes for the stage image (see ImageDataDb). */
    protected readonly stageImageData: ImageData = ImageDataDb.productFeatureExplorer;

    private readonly _resolvedAspect = withConfigFallback(this.config, 'imageAspectRatio', this.imageAspectRatio);
    private readonly _resolvedItems = withConfigFallback(this.config, 'items', this.items);

    protected readonly resolvedAspect = computed<number>(() => {
        const aspect = this._resolvedAspect();
        return typeof aspect === 'number' && Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
    });
    protected readonly resolvedItems = computed<IProductFeatureExplorerItem[]>(() => this._resolvedItems() ?? []);

    protected readonly panels = computed<FeaturePanel[]>(() =>
        this.resolvedItems().map((item, index) => ({
            index,
            number: item.number,
            title: item.title,
            teaser: item.teaser,
            detailsHtml: item.detailsHtml,
            hotspot: item.hotspot,
            regionId: `sir-product-feature-explorer-${this._instanceId}-panel-${item.number}`,
            markerLabel: `${item.number}: ${item.title}`,
        }))
    );

    /**
     * Split in halves rather than alternating sides: document order stays
     * 1..n, so the reading order and the tab order match what the eye sees.
     */
    protected readonly columns = computed<FeaturePanel[][]>(() => {
        const panels = this.panels();
        const leadingCount = Math.ceil(panels.length / 2);
        return [panels.slice(0, leadingCount), panels.slice(leadingCount)].filter((column) => column.length > 0);
    });

    protected readonly markers = computed<HotspotFeaturePanel[]>(() => this.panels().filter(hasHotspot));

    /**
     * The markers are one composite widget, not one tab stop each: `Tab` enters
     * the group once and the arrows move within it (WAI-ARIA roving tabindex),
     * which keeps the panel triggers below from being a second full pass over
     * the same features. The stop is the open feature's marker — the highlighted
     * one — and otherwise the marker the user last focused.
     */
    protected readonly markerTabStop = linkedSignal<number, number>({
        source: () => this.markers().findIndex((marker) => marker.number === this.active()),
        computation: (activeIndex, previous) => {
            if (activeIndex >= 0) return activeIndex;

            const lastFocused = previous?.value ?? 0;
            return lastFocused < this.markers().length ? lastFocused : 0;
        },
    });

    /**
     * One panel open at a time; markers and panel triggers read and write the
     * same state. The first feature starts open — the explorer never presents
     * fully collapsed — and a later items refresh keeps the panel the user
     * chose as long as its feature is still there.
     */
    protected readonly active = linkedSignal<IProductFeatureExplorerItem[], number | null>({
        source: this.resolvedItems,
        computation: (items, previous) => {
            if (!previous || previous.source.length === 0) return items[0]?.number ?? null;
            return items.some((item) => item.number === previous.value) ? previous.value : null;
        },
    });

    /** The one automatic pulse run: staggered starts, slow overlapping rings. */
    protected readonly tourRunning = signal(false);

    protected readonly pulseTiming = computed(() => {
        const step = Math.min(
            MAX_PULSE_STEP_SECONDS,
            AUTO_PULSE_BUDGET_SECONDS / (this.markers().length + PULSE_DURATION_RATIO - 1)
        );
        const duration = Math.min(MAX_PULSE_SECONDS, step * PULSE_DURATION_RATIO);
        return { step: `${Number(step.toFixed(2))}s`, duration: `${Number(duration.toFixed(2))}s` };
    });

    private readonly _destroyRef = inject(DestroyRef);
    private readonly _stage = viewChild<ElementRef<HTMLElement>>('stage');
    private readonly _panelElements: Signal<readonly ElementRef<HTMLElement>[]> = viewChildren('featurePanel', {
        read: ElementRef,
    });
    private readonly _markerButtons = viewChildren<ElementRef<HTMLButtonElement>>('markerButton');
    private readonly _instanceId = nextExplorerId++;

    constructor() {
        afterNextRender(() => {
            const stage = this._stage()?.nativeElement;
            if (!stage) return;

            const observer = new IntersectionObserver(
                (entries) => {
                    if (!entries.some((entry) => entry.isIntersecting)) return;
                    observer.disconnect();
                    this.tourRunning.set(true);
                },
                { threshold: 0.35 }
            );
            observer.observe(stage);
            this._destroyRef.onDestroy(() => observer.disconnect());
        });
    }

    protected setOpen(panel: FeaturePanel, open: boolean): void {
        this.active.set(open ? panel.number : null);
    }

    /**
     * The markers on the image are remote triggers: the panel one opens can
     * precede it in reading order (leading column), so a screen-reader user who
     * stays on the marker would never reach the revealed content by reading
     * forward. Opening moves focus to the panel's trigger — the feature title
     * and expanded state are announced, and the detail body is next in the
     * reading order. Closing reveals nothing, so focus stays where it is.
     */
    protected toggleFromMarker(panel: HotspotFeaturePanel): void {
        const opening = this.active() !== panel.number;
        this.setOpen(panel, opening);
        if (!opening) return;

        const panelElement = this._panelElements()[panel.index]?.nativeElement;
        panelElement?.querySelector<HTMLElement>(DISCLOSURE_TRIGGER_SELECTOR)?.focus();
    }

    protected onMarkerKeydown(event: KeyboardEvent, index: number): void {
        const nextIndex = getRovingIndex(event.key, index, this.markers().length, 'both');
        if (nextIndex === null) return;

        event.preventDefault();
        this._markerButtons()[nextIndex]?.nativeElement.focus();
    }

    protected onKeydown(event: KeyboardEvent): void {
        const target = event.target as HTMLElement;
        if (!target.matches(DISCLOSURE_TRIGGER_SELECTOR)) return;

        const triggers = Array.from(
            (event.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>(DISCLOSURE_TRIGGER_SELECTOR)
        );
        const currentIndex = triggers.indexOf(target as HTMLButtonElement);
        if (currentIndex === -1) return;

        const nextIndex = getRovingIndex(event.key, currentIndex, triggers.length, 'vertical');
        if (nextIndex === null) return;

        event.preventDefault();
        triggers[nextIndex]?.focus();
    }
}
